import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaTags,
  FaCss3,
  FaJs,
  FaExchangeAlt,
  FaExclamationTriangle
} from 'react-icons/fa';

const HTMLLesson10 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<style>
.cities {
  background-color: red;
  color: black;
  padding: 20px;
  margin: 20px;
}
</style>

<div class="cities">
  <h2>London</h2>
  <p>London is the capital of England.</p>
</div>

<div class="cities">
  <h2>Abuja</h2>
  <p>Abuja is the capital of Nigeria.</p>
</div>`;

  const code2 = `<!-- Multiple classes on one element -->
<div class="city featured highlighted">
  <h2>Paris</h2>
  <p>The city of lights.</p>
</div>

<!-- Different elements sharing same class -->
<h3 class="title">Section Title</h3>
<p class="title">This also uses title class</p>
<button class="title">Button with title class</button>`;

  const code3 = `/* CSS Class Selector Syntax */
.city-card {
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
}

.featured {
  background: #fff3cd;
  border-color: #ffc107;
}

.highlighted {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.title {
  font-weight: bold;
  color: #2c3e50;
}`;

  const code4 = `// JavaScript with Classes
// Get all elements with a class
const cities = document.getElementsByClassName('city');
const featuredCities = document.querySelectorAll('.featured');

// Loop through elements
for (let city of cities) {
  city.style.border = '2px solid blue';
}

// Add event listeners to all elements with class
document.querySelectorAll('.btn-primary').forEach(button => {
  button.addEventListener('click', function() {
    alert('Button clicked!');
  });
});`;

  const code5 = `<!-- Multiple Classes Example -->
<div class="card city featured">
  <h2 class="card-title">Tokyo</h2>
  <p class="card-text">Capital of Japan.</p>
  <button class="btn btn-primary">Visit</button>
</div>

<div class="card city">
  <h2 class="card-title">New York</h2>
  <p class="card-text">The big apple.</p>
  <button class="btn btn-secondary">Learn More</button>
</div>`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Reusable Class Styles */
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1rem;
    }
    
    .card {
      background: white;
      border-radius: 12px;
      padding: 2rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      margin-bottom: 2rem;
      border: 1px solid #e2e8f0;
    }
    
    .card.featured {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .btn-primary {
      background: #3b82f6;
      color: white;
    }
    
    .btn-primary:hover {
      background: #2563eb;
      transform: translateY(-2px);
    }
    
    .btn-secondary {
      background: #6b7280;
      color: white;
    }
    
    .text-center {
      text-align: center;
    }
    
    .mt-2 { margin-top: 2rem; }
    .mb-1 { margin-bottom: 1rem; }
  </style>
</head>
<body>
  <div class="container">
    <header class="text-center">
      <h1>World Cities Guide</h1>
      <p>Explore amazing cities around the world</p>
    </header>
    
    <main>
      <!-- Featured City Card -->
      <div class="card featured">
        <h2>🌆 London</h2>
        <p>London is the capital and largest city of England and the United Kingdom.</p>
        <button class="btn btn-primary">Explore London</button>
      </div>
      
      <!-- Regular City Cards -->
      <div class="card">
        <h2>🗼 Paris</h2>
        <p>Paris is the capital and most populous city of France.</p>
        <button class="btn btn-secondary">Visit Paris</button>
      </div>
      
      <div class="card">
        <h2>🗻 Tokyo</h2>
        <p>Tokyo is the capital and most populous prefecture of Japan.</p>
        <button class="btn btn-secondary">Discover Tokyo</button>
      </div>
    </main>
    
    <footer class="text-center mt-2">
      <p class="mb-1">&copy; 2024 World Cities Guide</p>
      <button class="btn btn-primary">Contact Us</button>
    </footer>
  </div>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Class Selectors Introduction",
      content: "The class attribute defines equal styles for multiple elements, allowing reusable styling across your HTML document",
      keyPoints: [
        "Multiple elements can share the same class",
        "Classes enable consistent, reusable styling",
        "One element can have multiple classes",
        "Case-sensitive and should use descriptive names"
      ],
      icon: <FaLayerGroup className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Multiple Classes & Reusability", 
      content: "Elements can have multiple class names, and different element types can share the same class for consistent styling",
      keyPoints: [
        "Add multiple classes: class='class1 class2 class3'",
        "Same class works on different element types",
        "Promotes consistent design system",
        "Separate concerns with different classes"
      ],
      icon: <FaTags className="section-icon" />,
      color: "#1d4ed8",
      code: code2
    },
    {
      title: "Class Selectors in CSS",
      content: "In CSS, select elements with a specific class using a dot (.) followed by the class name for styling",
      keyPoints: [
        "CSS syntax: .class-name { styles }",
        "Lower specificity than ID selectors",
        "Perfect for reusable component styling",
        "Can be combined for more specific targeting"
      ],
      icon: <FaCss3 className="section-icon" />,
      color: "#1e40af",
      code: code3
    },
    {
      title: "Class Selectors in JavaScript",
      content: "JavaScript can access and manipulate multiple elements using their class names for dynamic interactions",
      keyPoints: [
        "Get elements: document.getElementsByClassName('class')",
        "Modern approach: document.querySelectorAll('.class')",
        "Returns a collection of elements",
        "Essential for batch operations and dynamic content"
      ],
      icon: <FaJs className="section-icon" />,
      color: "#7c3aed",
      code: code4
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 10 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Class Selectors</h1>
              <p className="html-lesson-subtitle">
                Master reusable styling with class selectors for consistent, maintainable web design
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Class Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Class Selector Power
              </div>
              <h3 className="html-visual-title">
                One Class, Multiple Elements - Consistent Styling
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>CSS Class Definition</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="css"
                    text={`.city-card {
  background: #e3f2fd;
  border: 2px solid #2196f3;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem;
}`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Applied to Multiple Elements</h4>
                <div className="html-class-preview">
                  <div className="city-card-preview">
                    <h3>London</h3>
                    <p>Capital of England</p>
                  </div>
                  <div className="city-card-preview">
                    <h3>Paris</h3>
                    <p>Capital of France</p>
                  </div>
                  <div className="city-card-preview">
                    <h3>Tokyo</h3>
                    <p>Capital of Japan</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Note:</strong> Class names are case-sensitive! Use consistent naming conventions.</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for sections that have code */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Class vs ID Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaExchangeAlt className="html-badge-icon" />
                Class vs ID Comparison
              </div>
              <h3 className="html-comparison-title">
                When to Use Class vs ID Selectors
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Class Selector</th>
                    <th>ID Selector</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Syntax</strong></td>
                    <td><code>.class-name</code></td>
                    <td><code>#id-name</code></td>
                  </tr>
                  <tr>
                    <td><strong>Reusability</strong></td>
                    <td>✅ Multiple elements</td>
                    <td>❌ Single element only</td>
                  </tr>
                  <tr>
                    <td><strong>Multiple Values</strong></td>
                    <td>✅ <code>class="a b c"</code></td>
                    <td>❌ Only one ID per element</td>
                  </tr>
                  <tr>
                    <td><strong>CSS Specificity</strong></td>
                    <td>Medium (0,1,0)</td>
                    <td>High (1,0,0)</td>
                  </tr>
                  <tr>
                    <td><strong>JavaScript Access</strong></td>
                    <td><code>getElementsByClassName()</code></td>
                    <td><code>getElementById()</code></td>
                  </tr>
                  <tr>
                    <td><strong>Best For</strong></td>
                    <td>Reusable styles, components</td>
                    <td>Unique elements, page anchors</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Class Naming Best Practices
              </div>
              <h3 className="html-practices-title">
                Writing Effective Class Names
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Good Class Names</h4>
                <ul>
                  <li><code>btn-primary</code> (purpose-based)</li>
                  <li><code>card-header</code> (component-based)</li>
                  <li><code>text-center</code> (utility-based)</li>
                  <li><code>is-active</code> (state-based)</li>
                  <li><code>user-profile</code> (semantic)</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li><code>red-text</code> (style-based)</li>
                  <li><code>div1</code> (non-descriptive)</li>
                  <li><code>myClass</code> (inconsistent casing)</li>
                  <li><code>class123</code> (meaningless)</li>
                  <li><code>very-long-class-name</code> (too verbose)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Multiple Classes Example */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaTags className="html-badge-icon" />
                Multiple Classes
              </div>
              <h3 className="html-multiple-title">
                Combining Classes for Powerful Styling
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-code">
                <CopyBlock
                  language={language}
                  text={code5}
                  showLineNumbers={lineNumbers}
                  theme={dracula}
                  wrapLines={true}
                  codeBlock
                />
              </div>
              
              <div className="html-multiple-explanation">
                <h4>How It Works:</h4>
                <ul>
                  <li><strong>Base Class:</strong> <code>card</code> provides foundational styling</li>
                  <li><strong>Modifier Class:</strong> <code>featured</code> adds special styles</li>
                  <li><strong>Component Classes:</strong> <code>card-title</code>, <code>card-text</code> style child elements</li>
                  <li><strong>Utility Classes:</strong> <code>btn-primary</code>, <code>btn-secondary</code> for reusable buttons</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Website with Class Selectors
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code6}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered class selectors?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to use class selectors for reusable styling, multiple class combinations, and consistent design systems. Start building with classes!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-block-inline-elements" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson10;